import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Loader2, CheckCircle2, MessageSquarePlus } from "lucide-react";

function ManagedProjects() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/portal/ops/projects").then(r=>setRows(r.data.rows)).catch(()=>setRows([]));
  useEffect(() => { load(); }, []);
  if (!rows || rows.length === 0) return null;
  const approve = async (pid, mid) => {
    try { await api.post(`/portal/ops/projects/${pid}/milestones/${mid}/approve`); toast.success("Milestone approved"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const confirm = async (pid) => {
    try { await api.post(`/portal/ops/projects/${pid}/confirm-delivery`); toast.success("Delivery confirmed — thank you!"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const revision = async (pid) => {
    const note = window.prompt("What would you like us to revise?");
    if (!note || note.trim().length < 3) return;
    try { await api.post(`/portal/ops/projects/${pid}/request-revision`, { note }); toast.success("Revision request sent to the team"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-4" data-testid="portal-managed-projects">
      <h2 className="text-lg font-bold text-white" style={{fontFamily:'Outfit'}}>Managed by Optivize</h2>
      {rows.map(p => (
        <div key={p.id} className="card p-5">
          <div className="flex justify-between items-start flex-wrap gap-2">
            <div>
              <div className="text-cyan-400 text-xs uppercase tracking-wider font-mono">{p.number} · {p.service_category}</div>
              <h3 className="text-white font-semibold mt-1">{p.title}</h3>
            </div>
            <span className="tag capitalize">{p.status.replace(/_/g," ")}</span>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-emerald-500 to-cyan-400" style={{width:`${p.completion_pct}%`}}/></div>
            <span className="text-xs font-bold text-gray-300">{p.completion_pct}%</span>
          </div>
          <div className="mt-3 space-y-1.5 max-h-56 overflow-y-auto pr-1">
            {(p.milestones||[]).map(m => (
              <div key={m.id} className="flex items-center gap-2 text-xs">
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${m.status==='done' ? 'bg-emerald-400' : m.status==='in_progress' ? 'bg-amber-400' : 'bg-gray-600'}`}/>
                <span className={`flex-1 ${m.status==='done' ? "text-gray-200" : "text-gray-500"}`}>{m.title}{m.customer_note && <span className="text-gray-400"> — {m.customer_note}</span>}</span>
                {m.status === "done" && !m.customer_approved_at && (
                  <button onClick={()=>approve(p.id, m.id)} className="px-2 py-0.5 rounded bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-[10px] font-bold" data-testid={`portal-approve-ms-${m.id.slice(0,6)}`}>Approve</button>
                )}
                {m.customer_approved_at && <span className="text-[10px] text-emerald-400 font-bold">✓ approved</span>}
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-4">
            {p.status === "delivered" && (
              <button onClick={()=>confirm(p.id)} className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-xs font-bold inline-flex items-center gap-1.5" data-testid={`portal-confirm-${p.number}`}><CheckCircle2 size={13}/> Confirm delivery</button>
            )}
            <button onClick={()=>revision(p.id)} className="px-3 py-2 rounded-lg border border-slate-600 text-gray-300 text-xs font-bold inline-flex items-center gap-1.5" data-testid={`portal-revision-${p.number}`}><MessageSquarePlus size={13}/> Request revision</button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Projects() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", type: "website", description: "" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const { data } = await api.get("/projects/me");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/projects", form);
      toast.success("Project created");
      setOpen(false); setForm({ name: "", type: "website", description: "" }); load();
    } catch { toast.error("Failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-5">
      <ManagedProjects/>
      <div className="flex justify-between items-end">
        <h1 className="text-3xl font-black tracking-tighter text-white" style={{fontFamily:'Outfit'}}>Projects</h1>
        <button onClick={()=>setOpen(true)} className="btn-primary inline-flex items-center gap-2" data-testid="new-project-btn"><Plus size={16}/> New Project</button>
      </div>

      {items.length === 0 ? <div className="card p-8 text-center text-gray-400">No projects yet. Create one to start tracking milestones.</div> : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map(p => (
            <div key={p.id} className="card p-5">
              <div className="flex justify-between items-start">
                <div>
                  <div className="text-cyan-400 text-xs uppercase tracking-wider">{p.type}</div>
                  <h3 className="text-white font-semibold mt-1">{p.name}</h3>
                </div>
                <span className={`tag ${p.status==='in_progress' ? '' : '!text-emerald-300 !border-emerald-400/30 !bg-emerald-400/5'}`}>{p.status}</span>
              </div>
              <p className="text-gray-400 text-sm mt-2">{p.description}</p>
              <div className="mt-3 space-y-1.5">
                {(p.milestones||[]).map((m,i)=>(
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className={`w-1.5 h-1.5 rounded-full ${m.done ? 'bg-cyan-400' : 'bg-gray-600'}`}/>
                    <span className={m.done ? "text-gray-200" : "text-gray-500"}>{m.name}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-5" onClick={()=>setOpen(false)}>
          <form onClick={e=>e.stopPropagation()} onSubmit={create} className="card p-6 w-full max-w-md space-y-3">
            <h3 className="text-xl font-bold text-white">Create Project</h3>
            <div><label className="label">Name</label><input required value={form.name} onChange={e=>setForm({...form,name:e.target.value})} className="input w-full" data-testid="project-name"/></div>
            <div><label className="label">Type</label>
              <select value={form.type} onChange={e=>setForm({...form,type:e.target.value})} className="input w-full">
                <option value="website">Website</option><option value="app">App</option><option value="seo">SEO</option><option value="automation">Automation</option>
              </select>
            </div>
            <div><label className="label">Description</label><textarea value={form.description} onChange={e=>setForm({...form,description:e.target.value})} className="input w-full" rows={3}/></div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={()=>setOpen(false)} className="btn-ghost">Cancel</button>
              <button type="submit" disabled={loading} className="btn-primary inline-flex items-center gap-2">{loading ? <Loader2 size={14} className="animate-spin"/> : "Create"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
